import React, { createContext, useContext, useState, useRef, useEffect, useCallback, useMemo, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { logCriticalError } from '@/lib/errorLogger';
import { logAnalyticsEvent } from '@/hooks/useAnalytics';
import { getGlobalAudioElement } from '@/lib/audioManager';
import { 
  getCachedAudioUrl, 
  downloadAudioInBackground, 
  isCached, 
  getBlobUrl,
  isDownloading,
  removeCachedAudio,
} from '@/lib/audioCache';
import { useBriefQueueOptional, type QueueItem } from './BriefQueueContext';
import { useEpisodeQueueOptional, type EpisodeQueueItem } from './EpisodeQueueContext';
import { formatTime, waitForCanPlay } from '@/lib/audioUtils';
import { setupMediaSession, updatePositionState } from '@/lib/mediaSessionManager';
import { useEngagementTracking } from '@/hooks/useEngagementTracking';


export type PlaybackState = 'idle' | 'loading' | 'playing' | 'paused' | 'error' | 'preview_ended';
export type AudioType = 'summary' | 'full_episode';

export interface TrackInfo {
  masterBriefId: string;
  briefSlug?: string;
  voiceId?: string | null;
  title: string;
  artist: string;
  artwork: string | null;
  audioType: AudioType;
  audioUrl: string | null;
  audioDurationSeconds?: number | null;
  /** 'podcast' or 'youtube' — used for engagement tracking */
  source?: 'podcast' | 'youtube';
  /** For full_episode: the taddy_episode_uuid or youtube_video_id */
  contentId?: string;
  /** If set, playback will stop after this many seconds and show an auth CTA */
  previewDurationSeconds?: number;
}

interface AudioPlayerContextType {
  currentTrack: TrackInfo | null;
  playbackState: PlaybackState;
  currentTime: number;
  duration: number;
  speed: number;
  progress: number;
  volume: number;
  formattedTime: string;
  formattedDuration: string;
  formattedRemaining: string;
  isPlayerVisible: boolean;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  hasNextInQueue: boolean;
  hasPreviousInQueue: boolean;
  nextTrackTitle: string | null;
  skipToNext: () => Promise<void>;
  skipToPrevious: () => Promise<void>;
  playSummary: (track: Omit<TrackInfo, 'audioType'>) => Promise<void>;
  playFullEpisode: (track: Omit<TrackInfo, 'audioType'>, fromTime?: number) => Promise<void>;
  pause: () => void;
  resume: () => void;
  toggle: () => void;
  seek: (seconds: number) => void;
  seekForward: (seconds?: number) => void;
  seekBackward: (seconds?: number) => void;
  setSpeed: (speed: number) => void;
  setVolume: (volume: number) => void;
  closePlayer: () => void;
  retry: () => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | null>(null);

export function useGlobalAudioPlayer() {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useGlobalAudioPlayer must be used within AudioPlayerProvider');
  }
  return context;
}

export function useGlobalAudioPlayerOptional() {
  return useContext(AudioPlayerContext);
}

interface SavedProgress {
  currentTime: number;
  speed: number;
  savedAt: number;
}

export function AudioPlayerProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement>(getGlobalAudioElement());
  const briefQueue = useBriefQueueOptional();
  const episodeQueue = useEpisodeQueueOptional();
  const queryClient = useQueryClient();
  const engagement = useEngagementTracking();
  
  // Track info
  const [currentTrack, setCurrentTrack] = useState<TrackInfo | null>(null);
  
  // Playback state
  const [playbackState, setPlaybackState] = useState<PlaybackState>('idle');
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeedState] = useState(1);
  const [volume, setVolumeState] = useState(1);
  
  // UI state
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Refs
  const isLoadingRef = useRef(false);
  const loadingTimeoutRef = useRef<number | null>(null);
  const playRequestIdRef = useRef(0);
  const lastSrcRef = useRef<string | null>(null);
  const lastFormattedSecondRef = useRef<number>(-1);
  const backgroundDownloadStartedRef = useRef(false);
  const hasLoggedPlayRef = useRef(false);
  const playbackStateRef = useRef<PlaybackState>('idle');
  const isAutoAdvancingRef = useRef(false);
  const pendingAutoAdvanceRef = useRef<{
    type: 'summary' | 'episode';
    track: QueueItem | null;
    episode: EpisodeQueueItem | null;
    timestamp: number;
  } | null>(null);
  
  // Computed values
  const progress = duration > 0 ? Math.min(100, Math.max(0, (currentTime / duration) * 100)) : 0;
  const remaining = duration - currentTime;
  const formattedTime = formatTime(currentTime);
  const formattedDuration = duration > 0 ? formatTime(duration) : '--:--';
  const formattedRemaining = formatTime(remaining);
  const isPlayerVisible = currentTrack !== null;
  
  const getProgressKey = useCallback((track: TrackInfo | null) => {
    if (!track) return null;
    const voiceSuffix = track.audioType === 'summary' && track.voiceId ? `-${track.voiceId}` : '';
    return `audio-progress-${track.audioType}-${track.masterBriefId}${voiceSuffix}`;
  }, []);

  const getSummaryAudioCacheKey = useCallback((track: Pick<TrackInfo, 'masterBriefId' | 'voiceId'>) => {
    return track.voiceId ? `${track.masterBriefId}:${track.voiceId}` : track.masterBriefId;
  }, []);
  
  // Keep playbackStateRef in sync for event handlers
  useEffect(() => {
    playbackStateRef.current = playbackState;
  }, [playbackState]);
  
  // Load saved speed preference on mount
  useEffect(() => {
    try {
      const savedSpeed = localStorage.getItem('audio-playback-speed');
      if (savedSpeed) setSpeedState(parseFloat(savedSpeed));
    } catch (e) {
      console.error('Failed to load speed preference:', e);
    }
  }, []);
  
  // Save progress helper
  const saveProgress = useCallback(() => {
    const audio = audioRef.current;
    const progressKey = getProgressKey(currentTrack);
    if (!progressKey || !audio || audio.currentTime <= 0) return;

    // Treat near-end as completed: clear any saved progress so next play starts at 0.
    const dur = audio.duration && isFinite(audio.duration) && audio.duration > 0 ? audio.duration : 0;
    if (dur > 0 && (audio.currentTime >= dur - 15 || audio.currentTime / dur >= 0.97)) {
      localStorage.removeItem(progressKey);
      return;
    }

    const progress: SavedProgress = {
      currentTime: audio.currentTime,
      speed,
      savedAt: Date.now(),
    };
    localStorage.setItem(progressKey, JSON.stringify(progress));
  }, [currentTrack, speed, getProgressKey]);
  
  // Media Session setup — delegates to extracted utility
  const updateMediaSession = useCallback(() => {
    if (!currentTrack) return;
    
    setupMediaSession(
      audioRef.current,
      { title: currentTrack.title, artist: currentTrack.artist, artwork: currentTrack.artwork },
      {
        onPlay: () => {
          setPlaybackState('playing');
        },
        onPause: () => {
          setPlaybackState('paused');
          saveProgress();
        },
        onSeek: (time) => setCurrentTime(time),
      },
      speed,
    );
  }, [currentTrack, speed, saveProgress]);
  
  // Audio element event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      updateMediaSession();
    };

    const handleDurationChange = () => {
      if (audio.duration && isFinite(audio.duration) && audio.duration > 0) {
        setDuration(audio.duration);

        // Note: DB duration correction removed — client lacks UPDATE permission on master_briefs.
        // Duration is set correctly during audio generation in the backend pipeline.
      }
    };

    const handleTimeUpdate = () => {
      const time = audio.currentTime;
      const currentSecond = Math.floor(time);
      if (currentSecond !== lastFormattedSecondRef.current) {
        lastFormattedSecondRef.current = currentSecond;
        setCurrentTime(time);
      }
      
      // Preview duration limit: stop playback and show auth CTA
      if (currentTrack?.previewDurationSeconds && time >= currentTrack.previewDurationSeconds) {
        audio.pause();
        audio.currentTime = currentTrack.previewDurationSeconds;
        setPlaybackState('preview_ended');
        return;
      }
      
      // Accumulate listen time (only while playing, via ref to avoid stale closures)
      if (playbackStateRef.current === 'playing') {
        engagement.trackListenTime(time);
      }
      
      // Check engagement milestones
      engagement.checkMilestones(
        currentTrack ? { ...currentTrack, source: currentTrack.source || 'podcast' } : null,
        time,
        audio.duration,
      );
    };

    const handlePlaying = () => {
      isLoadingRef.current = false;
      if (loadingTimeoutRef.current) {
        window.clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      setPlaybackState('playing');
      updateMediaSession();
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'playing';
      }
      
      // Log analytics event once per track
      if (currentTrack && !hasLoggedPlayRef.current) {
        hasLoggedPlayRef.current = true;
        const eventType = currentTrack.audioType === 'summary' ? 'audio_played' : 'full_episode_played';
        logAnalyticsEvent({
          eventType,
          briefId: currentTrack.masterBriefId,
          metadata: {
            masterBriefId: currentTrack.masterBriefId,
            audioType: currentTrack.audioType,
            currentTime: audio.currentTime,
          },
        });
      }
    };

    const handlePause = () => {
      if (isLoadingRef.current) return;
      
      const hasValidDuration = audio.duration && isFinite(audio.duration) && audio.duration > 0;
      const isAtEnd = hasValidDuration && audio.currentTime >= audio.duration - 0.5;
      
      if (!isAtEnd) {
        setPlaybackState('paused');
      }
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'paused';
      }
    };

    const handleEnded = async () => {
      setPlaybackState('idle');
      setCurrentTime(0);
      const progressKey = getProgressKey(currentTrack);
      if (progressKey) localStorage.removeItem(progressKey);
      
      // Handle SUMMARY audio completion
      if (currentTrack?.audioType === 'summary') {
        const nextTrack = !isAutoAdvancingRef.current ? briefQueue?.getNextTrack() : null;
        
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            queryClient.invalidateQueries({ queryKey: ['library-briefs', user.id] });
          }
        } catch { /* ignore */ }
        
        briefQueue?.refreshQueue();
        
        if (nextTrack && !isAutoAdvancingRef.current) {
          if (document.visibilityState === 'visible') {
            isAutoAdvancingRef.current = true;
            setTimeout(() => {
              playTrackFromQueue(nextTrack);
              isAutoAdvancingRef.current = false;
            }, 500);
          } else {
            pendingAutoAdvanceRef.current = {
              type: 'summary', track: nextTrack, episode: null, timestamp: Date.now(),
            };
          }
        }
      }
      
      // Handle FULL EPISODE audio completion
      if (currentTrack?.audioType === 'full_episode') {
        const episodeUuid = currentTrack.masterBriefId.replace('episode-', '');
        const nextEpisode = !isAutoAdvancingRef.current ? episodeQueue?.getNextEpisode() : null;
        
        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            queryClient.invalidateQueries({ queryKey: ['saved-episodes', user.id] });
          }
        } catch (err) {
          console.error('[AudioPlayer] Failed to refresh episode data:', err);
        }
        
        episodeQueue?.refreshEpisodeQueue();
        
        if (nextEpisode && !isAutoAdvancingRef.current) {
          if (document.visibilityState === 'visible') {
            isAutoAdvancingRef.current = true;
            setTimeout(async () => {
              await playEpisodeFromQueue(nextEpisode);
              isAutoAdvancingRef.current = false;
            }, 500);
          } else {
            pendingAutoAdvanceRef.current = {
              type: 'episode', track: null, episode: nextEpisode, timestamp: Date.now(),
            };
          }
        }
      }
    };

    const handleError = () => {
      console.error('Audio error:', {
        code: audio.error?.code,
        message: audio.error?.message,
        src: audio.currentSrc?.substring(0, 100),
      });
      isLoadingRef.current = false;
      if (loadingTimeoutRef.current) {
        window.clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      setPlaybackState('error');
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('playing', handlePlaying);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('playing', handlePlaying);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [updateMediaSession, currentTrack, getProgressKey, engagement]);
  
  // Apply speed changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
    localStorage.setItem('audio-playback-speed', speed.toString());
  }, [speed]);
  
  // Apply volume changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);
  
  // Save progress on page unload
  useEffect(() => {
    const handleBeforeUnload = () => saveProgress();
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [saveProgress]);
  
  // Save progress periodically while playing
  useEffect(() => {
    if (playbackState !== 'playing') return;
    const interval = setInterval(saveProgress, 10000);
    return () => clearInterval(interval);
  }, [playbackState, saveProgress]);
  
  // Re-sync state when user returns + handle pending auto-advance
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const audio = audioRef.current;
        if (!audio) return;
        
        if (playbackState === 'playing' && audio.paused) {
          setPlaybackState('paused');
          if ('mediaSession' in navigator) {
            navigator.mediaSession.playbackState = 'paused';
          }
        }
        
        // Check for pending auto-advance
        const pending = pendingAutoAdvanceRef.current;
        if (pending) {
          pendingAutoAdvanceRef.current = null;
          
          const MAX_PENDING_AGE = 10 * 60 * 1000;
          if (Date.now() - pending.timestamp < MAX_PENDING_AGE && !isAutoAdvancingRef.current) {
            isAutoAdvancingRef.current = true;
            setTimeout(async () => {
              try {
                if (pending.type === 'summary' && pending.track) {
                  await playTrackFromQueue(pending.track);
                } else if (pending.type === 'episode' && pending.episode) {
                  await playEpisodeFromQueue(pending.episode);
                }
              } catch (err) {
                console.error('[AudioPlayer] Failed to resume auto-advance:', err);
              } finally {
                isAutoAdvancingRef.current = false;
              }
            }, 300);
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [playbackState]);
  
  // Refresh signed URL for summary audio
  const refreshSignedUrl = useCallback(async (masterBriefId: string, voiceId?: string | null): Promise<string | null> => {
    try {
      const { data, error } = await supabase.functions.invoke('get-signed-audio-url', {
        body: { masterBriefId, voiceId: voiceId || undefined },
      });
      if (error || !data?.signedUrl) return null;
      return data.signedUrl;
    } catch {
      return null;
    }
  }, []);
  
  // Core play function
  const playTrack = useCallback(async (track: TrackInfo, fromTime?: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    pendingAutoAdvanceRef.current = null;

    // Reset for new track
    const isNewTrack = !currentTrack || currentTrack.masterBriefId !== track.masterBriefId || currentTrack.audioType !== track.audioType;
    if (isNewTrack) {
      hasLoggedPlayRef.current = false;
      engagement.resetForNewTrack();
    }

    playRequestIdRef.current += 1;
    const requestId = playRequestIdRef.current;

    if (loadingTimeoutRef.current) {
      window.clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }

    setCurrentTime(0);
    lastFormattedSecondRef.current = -1;
    setCurrentTrack(track);
    if (track.audioDurationSeconds) setDuration(track.audioDurationSeconds);
    
    isLoadingRef.current = true;
    setPlaybackState('loading');

    // Safety timeout
    loadingTimeoutRef.current = window.setTimeout(() => {
      if (requestId !== playRequestIdRef.current) return;
      if (!isLoadingRef.current) return;
      isLoadingRef.current = false;
      setPlaybackState('paused');
    }, 8000);

    try {
      let urlToUse: string | null = null;
      let isFromCache = false;
      
      const attemptPlay = async (url: string): Promise<void> => {
        audio.pause();
        audio.currentTime = 0;
        audio.src = url;
        lastSrcRef.current = url;
        audio.load();
        await waitForCanPlay(audio);
        if (requestId !== playRequestIdRef.current) return;
      };

      // For summary audio, try cache first
      if (track.audioType === 'summary' && track.masterBriefId) {
        const cacheKey = getSummaryAudioCacheKey(track);
        const cachedUrl = getBlobUrl(cacheKey) || await getCachedAudioUrl(cacheKey);
        if (cachedUrl) {
          try {
            await attemptPlay(cachedUrl);
            urlToUse = cachedUrl;
            isFromCache = true;
          } catch (cacheError) {
            console.warn('[AudioPlayer] Cached audio failed, fetching fresh URL:', cacheError);
            await removeCachedAudio(cacheKey);
            lastSrcRef.current = null;
            isFromCache = false;
          }
        }
      }
      
      // Fall back to provided URL or fetch signed URL
      if (!urlToUse) {
        urlToUse = track.audioUrl;
        
        if (!urlToUse && track.audioType === 'summary') {
          urlToUse = await refreshSignedUrl(track.masterBriefId, track.voiceId);
          if (requestId !== playRequestIdRef.current) return;
        }
        
        if (!urlToUse) throw new Error('No audio URL available');
        await attemptPlay(urlToUse);
      }

      audio.playbackRate = speed;

      // Load saved progress or use provided time
      if (fromTime !== undefined && fromTime > 0) {
        audio.currentTime = fromTime;
      } else {
        const progressKey = getProgressKey(track);
        if (progressKey) {
          try {
            const saved = localStorage.getItem(progressKey);
            if (saved) {
              const parsed: SavedProgress = JSON.parse(saved);
              const sevenDays = 7 * 24 * 60 * 60 * 1000;
              if (Date.now() - parsed.savedAt < sevenDays && parsed.currentTime > 0) {
                // Treat near-end saved progress as completed — restart from 0.
                const knownDur =
                  audio.duration && isFinite(audio.duration) && audio.duration > 0
                    ? audio.duration
                    : (track.audioDurationSeconds && track.audioDurationSeconds > 0
                        ? track.audioDurationSeconds
                        : 0);
                const isCompleted =
                  knownDur > 0 &&
                  (parsed.currentTime >= knownDur - 15 || parsed.currentTime / knownDur >= 0.97);
                if (isCompleted) {
                  localStorage.removeItem(progressKey);
                } else {
                  audio.currentTime = parsed.currentTime;
                }
              }
            }
          } catch { /* ignore */ }
        }
      }

      await audio.play();
      
      if (requestId !== playRequestIdRef.current) {
        audio.pause();
        return;
      }

      isLoadingRef.current = false;
      if (loadingTimeoutRef.current) {
        window.clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      setPlaybackState('playing');

      // Background download for summary audio (not full episodes)
      if (track.audioType === 'summary' && !isFromCache && urlToUse && !urlToUse.startsWith('blob:')) {
        const cacheKey = getSummaryAudioCacheKey(track);
        if (!isCached(cacheKey) && !isDownloading(cacheKey)) {
          backgroundDownloadStartedRef.current = true;
          downloadAudioInBackground(
            cacheKey, urlToUse, track.audioDurationSeconds || undefined
          ).catch(err => {
            console.warn('[AudioPlayer] Background download failed:', err);
          });
        }
      }

    } catch (e) {
      if (requestId !== playRequestIdRef.current) return;
      
      const errorMessage = e instanceof Error ? e.message : 'Unknown error';
      const errorName = e instanceof Error ? e.name : '';
      
      // Suppress logging when the brief simply has no audio (failed pipeline
      // or not yet generated). The server already logged the real root cause;
      // re-logging here would create a misleading cascade entry.
      const isMissingAudio = errorMessage === 'No audio URL available';
      if (!isMissingAudio) {
        logCriticalError({
          errorCode: 'AUDIO_PLAYBACK_FAILED',
          errorMessage: `Audio playback failed: ${errorMessage}`,
          errorSource: 'edge_function',
          metadata: { masterBriefId: track.masterBriefId, audioType: track.audioType },
        });
      }
      
      isLoadingRef.current = false;
      if (loadingTimeoutRef.current) {
        window.clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      
      if (errorName === 'NotAllowedError' || errorMessage.includes('user gesture')) {
        setPlaybackState('paused');
      } else {
        setPlaybackState('error');
      }
    }
  }, [currentTrack, speed, refreshSignedUrl, getProgressKey, getSummaryAudioCacheKey, engagement]);
  
  // Queue helpers
  const playTrackFromQueue = useCallback(async (queueItem: QueueItem) => {
    const index = briefQueue?.findIndexByMasterBriefId(queueItem.masterBriefId, queueItem.voiceId);
    if (index !== undefined && index >= 0) briefQueue?.setCurrentIndex(index);
    
    await playTrack({
      masterBriefId: queueItem.masterBriefId,
      briefSlug: queueItem.briefSlug,
      voiceId: queueItem.voiceId || null,
      title: queueItem.title,
      artist: queueItem.artist,
      artwork: queueItem.artwork,
      audioType: 'summary',
      audioUrl: null,
      audioDurationSeconds: queueItem.audioDurationSeconds,
    });
  }, [playTrack, briefQueue]);
  
  const playEpisodeFromQueue = useCallback(async (queueItem: EpisodeQueueItem) => {
    const index = episodeQueue?.findEpisodeIndex(queueItem.taddyEpisodeUuid);
    if (index !== undefined && index >= 0) episodeQueue?.setCurrentEpisodeIndex(index);
    
    await playTrack({
      masterBriefId: `episode-${queueItem.taddyEpisodeUuid}`,
      title: queueItem.title,
      artist: queueItem.artist,
      artwork: queueItem.artwork,
      audioType: 'full_episode',
      audioUrl: queueItem.audioUrl,
      audioDurationSeconds: queueItem.audioDurationSeconds,
    });
  }, [playTrack, episodeQueue]);
  
  // Public play functions
  const playSummary = useCallback(async (track: Omit<TrackInfo, 'audioType'>) => {
    const index = briefQueue?.findIndexByMasterBriefId(track.masterBriefId, track.voiceId);
    if (index !== undefined && index >= 0) briefQueue?.setCurrentIndex(index);
    await playTrack({ ...track, audioType: 'summary' });
  }, [playTrack, briefQueue]);
  
  const playFullEpisode = useCallback(async (track: Omit<TrackInfo, 'audioType'>, fromTime?: number) => {
    const episodeUuid = track.masterBriefId.replace('episode-', '');
    const index = episodeQueue?.findEpisodeIndex(episodeUuid);
    if (index !== undefined && index >= 0) episodeQueue?.setCurrentEpisodeIndex(index);
    await playTrack({ ...track, audioType: 'full_episode' }, fromTime);
  }, [playTrack, episodeQueue]);
  
  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    playRequestIdRef.current += 1;
    isLoadingRef.current = false;
    if (loadingTimeoutRef.current) {
      window.clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    audio.pause();
    setPlaybackState('paused');
    saveProgress();
  }, [saveProgress]);
  
  const resume = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;
    
    if (audio.currentSrc && audio.readyState >= 2) {
      setPlaybackState('loading');
      try {
        await audio.play();
        setPlaybackState('playing');
      } catch {
        setPlaybackState('paused');
      }
    } else if (currentTrack) {
      await playTrack(currentTrack, audio.currentTime);
    }
  }, [currentTrack, playTrack]);
  
  const toggle = useCallback(() => {
    if (playbackState === 'loading') { pause(); return; }
    if (playbackState === 'playing') { pause(); } else { resume(); }
  }, [playbackState, pause, resume]);
  
  const seek = useCallback((seconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const maxTime = duration > 0 ? duration : Infinity;
    const newTime = Math.max(0, Math.min(seconds, maxTime));
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  }, [duration]);
  
  const seekForward = useCallback((seconds: number = 15) => {
    seek(currentTime + seconds);
  }, [seek, currentTime]);
  
  const seekBackward = useCallback((seconds: number = 15) => {
    seek(currentTime - seconds);
  }, [seek, currentTime]);
  
  const setSpeed = useCallback((newSpeed: number) => {
    setSpeedState(newSpeed);
  }, []);
  
  const setVolume = useCallback((newVolume: number) => {
    setVolumeState(Math.max(0, Math.min(1, newVolume)));
  }, []);
  
  const closePlayer = useCallback(() => {
    const audio = audioRef.current;
    if (audio) { audio.pause(); audio.src = ''; }
    saveProgress();
    setCurrentTrack(null);
    setPlaybackState('idle');
    setCurrentTime(0);
    setDuration(0);
    lastSrcRef.current = null;
    setIsExpanded(false);
  }, [saveProgress]);
  
  const retry = useCallback(() => {
    if (!currentTrack) return;
    lastSrcRef.current = null;
    playTrack(currentTrack);
  }, [currentTrack, playTrack]);
  
  // Queue navigation
  const skipToNext = useCallback(async () => {
    if (!briefQueue) return;
    saveProgress();
    const audio = audioRef.current;
    if (audio) { audio.pause(); audio.currentTime = 0; }
    const nextTrack = briefQueue.getNextTrack();
    if (nextTrack) await playTrackFromQueue(nextTrack);
  }, [briefQueue, playTrackFromQueue, saveProgress]);
  
  const skipToPrevious = useCallback(async () => {
    if (!briefQueue) return;
    saveProgress();
    const audio = audioRef.current;
    if (audio) { audio.pause(); audio.currentTime = 0; }
    const prevTrack = briefQueue.getPreviousTrack();
    if (prevTrack) await playTrackFromQueue(prevTrack);
  }, [briefQueue, playTrackFromQueue, saveProgress]);
  
  // Memoize queue navigation state to avoid recalculating on every render
  const hasNextInQueue = useMemo(() => Boolean(briefQueue?.getNextTrack()), [briefQueue?.getNextTrack]);
  const hasPreviousInQueue = useMemo(() => Boolean(briefQueue?.getPreviousTrack()), [briefQueue?.getPreviousTrack]);
  const nextTrackTitle = useMemo(() => briefQueue?.getNextTrack()?.title || null, [briefQueue?.getNextTrack]);
  
  const value: AudioPlayerContextType = {
    currentTrack,
    playbackState,
    currentTime,
    duration,
    speed,
    progress,
    volume,
    formattedTime,
    formattedDuration,
    formattedRemaining,
    isPlayerVisible,
    isExpanded,
    setIsExpanded,
    hasNextInQueue,
    hasPreviousInQueue,
    nextTrackTitle,
    skipToNext,
    skipToPrevious,
    playSummary,
    playFullEpisode,
    pause,
    resume,
    toggle,
    seek,
    seekForward,
    seekBackward,
    setSpeed,
    setVolume,
    closePlayer,
    retry,
  };
  
  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  );
}
